import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError, fileUrl } from "@/lib/apiClient";
import { toast } from "sonner";
import { Wrench, CheckCircle2, Upload, X, FileText, Loader2 } from "lucide-react";

export default function TechnicianApply() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", document: "", password: "", note: "", base_cep: "" });
  const [photo, setPhoto] = useState(null);   // { path, name }
  const [documents, setDocuments] = useState([]); // [{ path, name }]
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/technicians/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return { path: data.path, name: file.name };
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { setPhoto(await uploadFile(file)); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Falha no upload"); }
    finally { setUploading(false); }
  };

  const onDocs = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const up = [];
      for (const f of files) up.push(await uploadFile(f));
      setDocuments((d) => [...d, ...up]);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Falha no upload"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("A senha deve ter pelo menos 6 caracteres."); return; }
    setLoading(true);
    try {
      const payload = { ...form };
      if (payload.document) payload.document = payload.document.replace(/\D/g, "");
      else delete payload.document;
      if (photo) payload.photo = photo.path;
      if (documents.length) payload.documents = documents.map((d) => d.path);
      await api.post("/technicians/apply", payload);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-6">
          <div className="h-10 w-10 rounded-xl bg-emerald-600 flex items-center justify-center">
            <Wrench className="h-5 w-5 text-white" />
          </div>
          <span className="text-lg font-bold text-slate-800">Clima Branca · Técnicos</span>
        </div>
        <Card className="border-slate-200 shadow-sm">
          {done ? (
            <CardContent className="p-8 text-center" data-testid="apply-success">
              <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-slate-800">Cadastro enviado!</h1>
              <p className="text-sm text-slate-500 mt-1">
                Recebemos sua inscrição. O gestor vai analisar e definir sua área de atuação.
                Você já pode entrar no app para acompanhar o status.
              </p>
              <Button className="mt-6 w-full bg-blue-600 hover:bg-blue-700" onClick={() => navigate("/")}
                data-testid="apply-go-login">Ir para o login</Button>
            </CardContent>
          ) : (
            <>
              <CardHeader>
                <CardTitle className="text-xl">Seja um técnico credenciado</CardTitle>
                <p className="text-sm text-slate-500">Cadastre-se para atender chamados na sua região.</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={submit} className="space-y-3.5">
                  <div className="space-y-1.5"><Label>Nome completo</Label>
                    <Input required value={form.name} onChange={upd("name")} data-testid="apply-name-input" /></div>
                  <div className="space-y-1.5"><Label>E-mail</Label>
                    <Input type="email" required value={form.email} onChange={upd("email")} data-testid="apply-email-input" /></div>
                  <div className="space-y-1.5"><Label>Telefone / WhatsApp</Label>
                    <Input required value={form.phone} onChange={upd("phone")} placeholder="(21) 99999-9999" data-testid="apply-phone-input" /></div>
                  <div className="space-y-1.5"><Label>CPF ou CNPJ (opcional)</Label>
                    <Input value={form.document} onChange={upd("document")} placeholder="000.000.000-00" data-testid="apply-document-input" /></div>
                  <div className="space-y-1.5"><Label>Senha</Label>
                    <Input type="password" required value={form.password} onChange={upd("password")} placeholder="Mínimo 6 caracteres" data-testid="apply-password-input" /></div>
                  <div className="space-y-1.5"><Label>CEP-base (sua localização)</Label>
                    <Input value={form.base_cep} onChange={upd("base_cep")} placeholder="00000-000" data-testid="apply-basecep-input" />
                    <p className="text-[11px] text-slate-400">Usamos para priorizar os chamados mais próximos de você.</p></div>

                  <div className="space-y-1.5">
                    <Label>Foto (perfil)</Label>
                    <div className="flex items-center gap-3">
                      {photo && <img src={fileUrl(photo.path)} alt="foto" className="h-12 w-12 rounded-full object-cover border" />}
                      <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 text-sm cursor-pointer hover:bg-slate-50" data-testid="apply-photo-label">
                        <Upload className="h-4 w-4" /> {photo ? "Trocar" : "Enviar foto"}
                        <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="apply-photo-input" />
                      </label>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label>Documentos (CNPJ, certificados)</Label>
                    <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 text-sm cursor-pointer hover:bg-slate-50" data-testid="apply-docs-label">
                      <Upload className="h-4 w-4" /> Anexar documentos
                      <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={onDocs} data-testid="apply-docs-input" />
                    </label>
                    {documents.map((d, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-slate-50 border border-slate-100 rounded px-2 py-1.5" data-testid={`apply-doc-${i}`}>
                        <span className="flex items-center gap-1.5 truncate"><FileText className="h-3.5 w-3.5 text-slate-400" /> {d.name}</span>
                        <button type="button" className="text-slate-400 hover:text-red-500" onClick={() => setDocuments(documents.filter((_, idx) => idx !== i))}><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-1.5"><Label>Região onde deseja atuar</Label>
                    <Textarea rows={2} value={form.note} onChange={upd("note")}
                      placeholder="Ex.: Rio de Janeiro - Zona Sul e Centro; CEPs 20000 a 22999" data-testid="apply-note-input" /></div>
                  <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading || uploading}
                    data-testid="apply-submit-button">
                    {uploading ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" />Enviando arquivos...</> : (loading ? "Enviando..." : "Enviar cadastro")}
                  </Button>
                </form>
                <p className="text-sm text-slate-500 text-center mt-5">
                  Já tem cadastro?{" "}
                  <Link to="/" className="text-blue-600 font-medium hover:underline" data-testid="apply-go-login-link">Entrar</Link>
                </p>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
